import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import '../styles/NewsDetail.css';

const NewsDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [news, setNews] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchNews();
    }, [slug]);

    const fetchNews = async () => {
        try {
            const response = await fetch(`/api/news/${slug}`);
            const data = await response.json();
            
            if (data.success) {
                setNews(data.article);
            } else {
                setError('Noticia no encontrada');
            }
        } catch (error) {
            console.error('Error fetching news:', error);
            setError('Error cargando la noticia');
        } finally {
            setLoading(false);
        }
    };

    const handleShare = async () => {
        const url = window.location.href;
        const title = news.title;
        const text = news.summary;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: title,
                    text: text,
                    url: url,
                });
            } catch (error) {
                console.log('Error sharing:', error);
            }
        } else {
            // Fallback: copy to clipboard
            try {
                await navigator.clipboard.writeText(url);
                alert('Enlace copiado al portapapeles');
            } catch (error) {
                console.log('Error copying to clipboard:', error);
            }
        }
    };

    if (loading) {
        return (
            <div className="news-detail loading">
                <div className="loading-spinner">Cargando...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="news-detail error">
                <h2>Error</h2>
                <p>{error}</p>
                <button onClick={() => navigate('/')} className="back-btn">
                    Volver al inicio
                </button>
            </div>
        );
    }

    if (!news) {
        return (
            <div className="news-detail error">
                <h2>Noticia no encontrada</h2>
                <button onClick={() => navigate('/')} className="back-btn">
                    Volver al inicio
                </button>
            </div>
        );
    }

    const articleUrl = window.location.href;
    const imageUrl = news.cover_image || 'https://primarturogro.edu.mx/uploads/LogoArturo.png';

    return (
        <div className="news-detail">
            <Helmet>
                <title>{news.title} - Esc. Prim. Arturo Guerrero González T.M.</title>
                <meta name="description" content={news.summary} />
                <meta name="keywords" content={`${news.title}, escuela, primaria, Arturo Guerrero González, noticias`} />
                <meta name="author" content={news.author_name} />
                
                {/* Open Graph / Facebook */}
                <meta property="og:type" content="article" />
                <meta property="og:title" content={news.title} />
                <meta property="og:description" content={news.summary} />
                <meta property="og:image" content={imageUrl} />
                <meta property="og:url" content={articleUrl} />
                <meta property="og:site_name" content="Esc. Prim. Arturo Guerrero González T.M." />
                
                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={news.title} />
                <meta name="twitter:description" content={news.summary} />
                <meta name="twitter:image" content={imageUrl} />
                
                {/* Article specific */}
                <meta property="article:author" content={news.author_name} />
                <meta property="article:published_time" content={news.created_at} />
                <meta property="article:modified_time" content={news.updated_at} />
                <meta property="article:section" content="Noticias" />
                <meta property="article:tag" content="Escuela" />
                <meta property="article:tag" content="Educación" />
            </Helmet>

            <article className="news-article">
                <header className="news-header">
                    <div className="news-meta">
                        <button onClick={() => navigate('/')} className="back-btn">
                            ← Volver
                        </button>
                        <div className="news-info">
                            <span className="news-author">Por: {news.author_name}</span>
                            <span className="news-date">
                                {new Date(news.created_at).toLocaleDateString('es-ES', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </span>
                        </div>
                    </div>
                    
                    <h1 className="news-title">{news.title}</h1>
                    
                    <div className="news-summary">
                        <p>{news.summary}</p>
                    </div>

                    {news.cover_image && (
                        <div className="news-cover-image">
                            <img src={news.cover_image} alt={news.title} />
                        </div>
                    )}
                </header>

                <div className="news-content">
                    <div 
                        className="news-body"
                        dangerouslySetInnerHTML={{ __html: news.content }}
                    />
                </div>

                <footer className="news-footer">
                    <div className="news-actions">
                        <button onClick={handleShare} className="share-btn">
                            📤 Compartir
                        </button>
                        <button onClick={() => navigate('/')} className="back-btn">
                            ← Volver al inicio
                        </button>
                    </div>
                    
                    <div className="news-updated">
                        {news.updated_at !== news.created_at && (
                            <p>
                                Última actualización: {new Date(news.updated_at).toLocaleDateString('es-ES', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })}
                            </p>
                        )}
                    </div>
                </footer>
            </article>
        </div>
    );
};

export default NewsDetail;
